import React, { useEffect, useState } from "react";
import {
  X,
  Pencil,
  Trash2,
  Power,
  Mail,
  Phone,
  Building2,
  GraduationCap,
  User,
  ShieldCheck,
  Smartphone,
  Wallet,
  Activity,
} from "lucide-react";
import {
  getTeacherAccountById,
  deleteTeacherAccount,
  toggleTeacherAccountStatus,
} from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasPermission } from "../../../utils/permissionUtils";
import EmployeeDocumentAccessPanel from "../../hrm/employees/EmployeeDocumentAccessPanel";
import TeacherDevicePanel from "./TeacherDevicePanel";
import TeacherAccountUsagePanel from "./TeacherAccountUsagePanel";
import { labelForBankCode } from "../../../data/vietnamBanks";

const TeacherAccountDetailModal = ({
  isOpen,
  employeeId,
  onClose,
  onEdit,
  onDeleted,
  initialTab = "account",
}) => {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (!isOpen || !employeeId) {
      setRow(null);
      return;
    }
    setActiveTab(initialTab);
    (async () => {
      setLoading(true);
      try {
        const data = await getTeacherAccountById(employeeId);
        setRow(data);
      } catch {
        showErrorToast("Không tải được chi tiết.");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, employeeId, onClose, initialTab]);

  if (!isOpen) return null;

  const handleDelete = async () => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.teacher_account,
      name: row?.name,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteTeacherAccount(employeeId);
      showSuccessToast("Đã xóa tài khoản.");
      onDeleted?.();
      onClose();
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không xóa được.");
    }
  };

  const handleToggleStatus = async () => {
    try {
      await toggleTeacherAccountStatus(employeeId, !row?.is_active);
      showSuccessToast("Đã cập nhật trạng thái.");
      setRow((r) => (r ? { ...r, is_active: !r.is_active } : r));
      onDeleted?.();
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không cập nhật được.");
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative flex h-[min(90vh,820px)] max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white bg-slate-50 shadow-2xl">
        <div className="shrink-0 bg-white px-8 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center ring-2 ring-gray-100">
              <GraduationCap className="size-7 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Chi tiết tài khoản giáo viên đối tác
              </h2>
              <p className="text-sm text-gray-500">
                Mã nhân viên: <span className="font-semibold">{row?.code || "—"}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"
          >
            <X size={22} />
          </button>
        </div>

        {!loading && row ? (
          <div className="shrink-0 flex border-b border-gray-100 bg-white px-8">
            <button
              type="button"
              onClick={() => setActiveTab("account")}
              className={`mr-6 border-b-2 px-1 py-3 text-xs font-bold uppercase tracking-wide transition ${
                activeTab === "account"
                  ? "border-red-600 text-red-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <User size={14} />
                Thông tin tài khoản
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("usage")}
              className={`border-b-2 px-1 py-3 text-xs font-bold uppercase tracking-wide transition ${
                activeTab === "usage"
                  ? "border-red-600 text-red-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <Activity size={14} />
                Sử dụng phần mềm
              </span>
            </button>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
          {loading || !row ? (
            <div className="flex h-full min-h-[320px] items-center justify-center p-12 text-center text-gray-500">
              Đang tải...
            </div>
          ) : activeTab === "usage" ? (
            <div className="p-8">
              <TeacherAccountUsagePanel employeeId={row.id} />
            </div>
          ) : (
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-gray-800 font-bold uppercase text-sm">
                  <User size={16} className="text-red-600" />
                  Thông tin cơ bản
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <p>
                    <span className="text-gray-500">Họ tên:</span>{" "}
                    <span className="font-medium text-gray-800">{row.name || "—"}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Trường:</span>{" "}
                    <span className="font-medium text-gray-800">
                      {row.partner_school_name || "—"}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Chi nhánh:</span>{" "}
                    <span className="font-medium text-gray-800">{row.branch_name || "—"}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Trạng thái:</span>{" "}
                    <span
                      className={`font-semibold ${
                        row.is_active ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {row.is_active ? "Đang hoạt động" : "Ngưng sử dụng"}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Thiết bị APP:</span>{" "}
                    <span className="font-medium text-gray-800">
                      1 máy tính + 1 điện thoại
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-gray-800 font-bold uppercase text-sm">
                  <Mail size={16} className="text-red-600" />
                  Liên hệ
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <p className="flex items-center gap-2">
                    <Mail size={15} className="text-red-500 shrink-0" />
                    <span className="font-medium text-gray-800">{row.email || "—"}</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone size={15} className="text-red-500 shrink-0" />
                    <span className="font-medium text-gray-800">{row.phone || "—"}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Ngày sinh:</span>{" "}
                    <span className="font-medium text-gray-800">
                      {row.birthdate ? new Date(row.birthdate).toLocaleDateString("vi-VN") : "—"}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Giới tính:</span>{" "}
                    <span className="font-medium text-gray-800">
                      {row.gender === "male"
                        ? "Nam"
                        : row.gender === "female"
                          ? "Nữ"
                          : row.gender || "—"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4 md:col-span-2">
                <div className="flex items-center gap-2 text-gray-800 font-bold uppercase text-sm">
                  <Wallet size={16} className="text-red-600" />
                  Tài khoản nhận lương
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <p>
                    <span className="text-gray-500">Ngân hàng:</span>{" "}
                    <span className="font-medium text-gray-800">
                      {labelForBankCode(row.payroll_bank_code) || "—"}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Số tài khoản:</span>{" "}
                    <span className="font-medium text-gray-800">
                      {row.payroll_bank_account_number || "—"}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Tên tài khoản:</span>{" "}
                    <span className="font-medium text-gray-800">
                      {row.payroll_bank_account_name || "—"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4 md:col-span-2">
                <div className="flex items-center gap-2 text-gray-800 font-bold uppercase text-sm">
                  <Building2 size={16} className="text-red-600" />
                  Đối tác & ghi chú
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p>
                    <span className="text-gray-500">Khách hàng (trường):</span>{" "}
                    <span className="font-medium text-gray-800">{row.partner_school_name || "—"}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Chi nhánh quản lý:</span>{" "}
                    <span className="font-medium text-gray-800">{row.branch_name || "—"}</span>
                  </p>
                  <p className="md:col-span-2">
                    <span className="text-gray-500">Ghi chú:</span>{" "}
                    <span className="font-medium text-gray-800">{row.remark || "—"}</span>
                  </p>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-800 font-bold uppercase text-sm mb-3">
                    <Smartphone size={16} className="text-red-600" />
                    Thiết bị đăng nhập APP
                  </div>
                  <TeacherDevicePanel employeeId={row.id} />
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-800 font-bold uppercase text-sm mb-3">
                    <ShieldCheck size={16} className="text-red-600" />
                    Quản lý lượt tài liệu
                  </div>
                  <EmployeeDocumentAccessPanel employeeId={row.id} />
                </div>
              </div>
            </div>
          )}
        </div>

        {!loading && row ? (
          <div className="shrink-0 bg-white px-8 py-4 border-t border-gray-100 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={!hasPermission("crm.teacher_accounts", "U")}
              className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              <Power size={16} />
              {row.is_active ? "Ngưng sử dụng" : "Kích hoạt"}
            </button>
            {hasPermission("crm.teacher_accounts", "U") && (
              <button
                type="button"
                onClick={() => onEdit?.(row.id)}
                className="px-4 py-2 rounded-xl border border-blue-200 text-blue-700 hover:bg-blue-50 flex items-center gap-2"
              >
                <Pencil size={16} />
                Chỉnh sửa
              </button>
            )}
            {hasPermission("crm.teacher_accounts", "D") && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 rounded-xl border border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 size={16} />
                Xóa
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TeacherAccountDetailModal;
